import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@/lib/firebase';

export interface ImageUploadOptions {
  maxSize?: number; // Max file size in bytes (default: 5MB)
  allowedTypes?: string[]; // Allowed MIME types
  maxImages?: number; // Maximum number of images allowed
}

const DEFAULT_OPTIONS: ImageUploadOptions = {
  maxSize: 5 * 1024 * 1024, // 5MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
  maxImages: 5,
};

/**
 * Validate image file
 */
const validateImage = (file: File, options: ImageUploadOptions): string | null => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  if (!opts.allowedTypes?.includes(file.type)) {
    return `Invalid file type. Allowed types: ${opts.allowedTypes.join(', ')}`;
  }
  
  if (opts.maxSize && file.size > opts.maxSize) {
    return `File too large. Maximum size: ${(opts.maxSize / (1024 * 1024)).toFixed(1)}MB`;
  }
  
  return null;
};

/**
 * Resize image to reduce file size
 */
const resizeImage = (file: File, maxWidth: number = 800, maxHeight: number = 600, quality: number = 0.8): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        } else {
          resolve(file);
        }
      }, file.type, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Upload multiple images for reviews
 */
export const uploadReviewImages = async (
  files: File[],
  userId: string,
  productId: string,
  options: ImageUploadOptions = {}
): Promise<string[]> => {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  // Validate number of files
  if (opts.maxImages && files.length > opts.maxImages) {
    throw new Error(`Too many images. Maximum allowed: ${opts.maxImages}`);
  }
  
  const uploadPromises = files.map(async (file, index) => {
    // Validate each file
    const validationError = validateImage(file, opts);
    if (validationError) {
      throw new Error(validationError);
    }
    
    // Resize image to reduce file size
    const resizedFile = await resizeImage(file);
    
    // Generate unique filename
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2, 15);
    const fileName = `${timestamp}_${randomId}_${index}`;
    const fileExtension = resizedFile.name.split('.').pop();
    
    // Create storage reference
    const storageRef = ref(storage, `reviews/${userId}/${productId}/${fileName}.${fileExtension}`);
    
    // Upload file
    await uploadBytes(storageRef, resizedFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  });
  
  return Promise.all(uploadPromises);
};

/**
 * Delete a single review image
 */
export const deleteReviewImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extract storage path from URL
    const urlParts = imageUrl.split('/o/')[1]?.split('?')[0];
    if (urlParts) {
      const path = decodeURIComponent(urlParts);
      const storageRef = ref(storage, path);
      await deleteObject(storageRef);
      console.log('Successfully deleted review image:', path);
    }
  } catch (error) {
    console.warn('Failed to delete review image:', imageUrl, error);
    // Don't throw error to prevent blocking other operations
  }
};

/**
 * Delete multiple review images
 */
export const deleteReviewImages = async (imageUrls: string[]): Promise<void> => {
  const deletePromises = imageUrls.map(deleteReviewImage);
  await Promise.all(deletePromises);
};

/**
 * Generate image preview URL
 */
export const generateImagePreview = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Clean up preview URLs
 */
export const cleanupImagePreviews = (urls: string[]): void => {
  urls.forEach(url => {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  });
};